import React, { ReactNode, useMemo } from 'react';
import { styled, SxProps } from '@mui/system';
import useEmblaCarousel from 'embla-carousel-react';
import { usePrevNextButtons, CarouselArrowButton } from './CarouselArrowButtons';
import { Box } from '../Box';
import { EmblaOptionsType } from 'embla-carousel';

export const EmblaCarousel = styled('div')({
    overflow: 'hidden',
    flex: '1 1 0',
    pointerEvents: 'none'
});

export const EmblaContainer = styled('div')({
    display: 'flex',
    pointerEvents: 'none',
    '& > *': {
        pointerEvents: 'auto'
    }
});

export interface CarouselProps {
    children: ReactNode;
    selectedIndex?: number;
    slides?: ReactNode[];
    sx?: SxProps;
}

export const Carousel = ({ children, selectedIndex, sx }: CarouselProps) => {
    // NOTE: these docs are for v8.6.0. The main docs page is updated to the release-candidate docs,
    // which are very different than the version we're using (as of 5.Mar.2026)
    // Options: https://github.com/davidjerleke/embla-carousel/blob/v8.6.0/packages/embla-carousel-docs/src/content/pages/api/options.mdx
    // Methods: https://github.com/davidjerleke/embla-carousel/blob/v8.6.0/packages/embla-carousel-docs/src/content/pages/api/methods.mdx
    // Additional docs can be found navigating the file structure above.

    const options = useMemo(() => {
        const options: EmblaOptionsType = { align: 'start' };
        if (selectedIndex) return { ...options, startIndex: selectedIndex };
        return options;
    }, [selectedIndex]);
    const [emblaRef, emblaApi] = useEmblaCarousel(options);

    const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={[
                    {
                        width: '100%',
                        display: 'flex',
                        pointerEvents: 'none'
                    },
                    ...(Array.isArray(sx) ? sx : [sx])
                ]}
            >
                <CarouselArrowButton
                    data-testid="click-prev"
                    direction="prev"
                    onClick={onPrevButtonClick}
                    disabled={prevBtnDisabled}
                />
                <EmblaCarousel ref={emblaRef}>
                    <EmblaContainer>{children}</EmblaContainer>
                </EmblaCarousel>
                <CarouselArrowButton
                    data-testid="click-next"
                    direction="next"
                    onClick={onNextButtonClick}
                    disabled={nextBtnDisabled}
                />
            </Box>
        </Box>
    );
};
